import { HttpErrorResponse, HttpHandlerFn, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { Injector, inject } from '@angular/core';
import { catchError, switchMap, throwError } from 'rxjs';
import { AuthService } from '../services/auth.service';

/**
 * Intercepts 401 Unauthorized responses and triggers an OIDC token refresh.
 * Handles scenarios like auth-server key rotation (ephemeral keys in dev) where the access
 * token signature becomes invalid mid-session.
 *
 * The refresh itself is the single-flight `AuthService.forceRefreshSession()` — the ONE home
 * shared with the WebSocket auth-recovery corridor — so concurrent 401s and any socket refusal
 * join one token grant rather than each issuing a parallel grant against the rolling refresh
 * token. This interceptor keeps only the request-level loop guard:
 *
 * Flow: 401 → shared refresh → retry once → if still 401 → give up (no redirect loop).
 */

const RETRY_HEADER = 'X-Retry-After-Refresh';

export const unauthorizedInterceptor: HttpInterceptorFn = (req: HttpRequest<unknown>, next: HttpHandlerFn) => {
  // Resolve AuthService LAZILY (only when a 401 is actually handled), never at interceptor
  // entry: AuthService's construction drives OIDC, which uses HttpClient, whose interceptor
  // chain is this very function — injecting AuthService up front closes that loop (NG0200).
  // By the time a 401 is caught the app is running and AuthService is fully constructed, so
  // injector.get(AuthService) is cycle-free (and, unlike inject(), needs no injection context).
  const injector = inject(Injector);

  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {
      if (error.status !== 401) {
        return throwError(() => error);
      }

      // Only attempt refresh for API/GraphQL requests
      if (!req.url.includes('/api') && !req.url.includes('/graphql')) {
        return throwError(() => error);
      }

      // If this request was already retried after a refresh, do NOT retry again — break the loop
      if (req.headers.has(RETRY_HEADER)) {
        return throwError(() => error);
      }

      // Clone the request with a retry marker so the retried request won't trigger another refresh
      const retryReq = req.clone({
        setHeaders: { [RETRY_HEADER]: '1' }
      });

      return injector.get(AuthService).forceRefreshSession().pipe(
        switchMap(authenticated => authenticated ? next(retryReq) : throwError(() => error))
      );
    })
  );
};
